import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import clsx from 'clsx';
import TabFilter from 'views/EMR/components/TabFilter';
import Button from 'pages/Dashboard/components/Button';
import MediaTypesList from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog/MediaTypesList';
import {
  FileUploadDialogContext,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog/columns';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  useUploadBulkMultimedia,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  useNotify,
} from 'core/hooks';
import {
  PatientMultimediaTypeDTO,
  PatientMultiMediaResponseDTO,
  UploadErrorDTO,
} from 'dtos';
import {
  isEmptyString,
  pluralize,
  sanitizeFileName,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import {
  useGetMediaCategoryTabOptions,
  useGetMediaTypesList,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';
import Preview from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog/Preview';
import SelectableProvider from 'core/context/selectable';
import uniqueId from 'lodash/uniqueId';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import compact from 'lodash/compact';
import Alert from 'pages/Dashboard/components/Alert';

export type MultimediaTypeWithCategory = PatientMultimediaTypeDTO & {
  category: string;
  files?: FileLocalUrl[];
};

type FilesByMediaType = Record<string, FileLocalUrl[]>;

export type AddFileProps = {
  files: FileLocalUrl[];
  original: MultimediaTypeWithCategory;
};

type ValidationResult = {
  validFiles: FileLocalUrl[];
  invalidFiles: FileLocalUrl[];
};

function validateFiles(
  files: FileLocalUrl[],
  allowedFileTypes: string[],
): ValidationResult {
  const allowed = allowedFileTypes.map((ext) => ext.trim().toLowerCase());

  const validFiles: FileLocalUrl[] = [];
  const invalidFiles: FileLocalUrl[] = [];

  files.forEach((file) => {
    const ext = `.${file?.file?.name?.split('.').pop()?.toLowerCase()}`;

    if (allowed.includes(ext)) {
      validFiles.push(file);
    } else {
      invalidFiles.push(file);
    }
  });

  return { validFiles, invalidFiles };
}

function getMediaTypeKey(mediaType: MultimediaTypeWithCategory): string {
  return mediaType.label ?? mediaType.value ?? '';
}

export type FileUploadDialogProps = {
  dropzoneAccept?: Record<string, string[]>;
  patientId: number;
  onUpdate?: (newMediaList: PatientMultiMediaResponseDTO[]) => void;
  defaultCategory?: string;
  hasBackdrop?: boolean;
  dialogHeight?: number;
};

type Props = FileUploadDialogProps & {
  onClose: () => void;
};

export default function FileUploadDialog({
  onClose,
  dropzoneAccept,
  patientId,
  onUpdate,
  defaultCategory,
  hasBackdrop = true,
  dialogHeight,
}: Props) {
  const { mutateAsync: uploadBulk, isPending: isSaving } = useUploadBulkMultimedia();
  const notify = useNotify();
  const [selectedCategory, selectCategory] = useState<string>('');
  const [uploadErrors, setUploadErrors] = useState<UploadErrorDTO[]>([]);
  const [filesByMediaType, setFilesByMediaType] = useState<FilesByMediaType>({});

  const tabOptions = useGetMediaCategoryTabOptions();
  const mediaTypes = useGetMediaTypesList();

  const allowedFileTypes = useGetAllowedFileTypes();

  const mediaTypesWithFiles = useMemo(
    () => mediaTypes.map((mediaType) => ({
      ...mediaType,
      files: filesByMediaType[getMediaTypeKey(mediaType)] ?? mediaType.files,
    })),
    [filesByMediaType, mediaTypes],
  );

  const updateData = useCallback(
    (original: MultimediaTypeWithCategory, files: FileLocalUrl[]) => {
      const {
        validFiles,
        invalidFiles,
      } = validateFiles(files, allowedFileTypes.split(','));

      const mediaTypeKey = getMediaTypeKey(original);
      const updatedFileList = validFiles.map((file) => ({
        ...file,
        id: file.id ?? uniqueId(file.name ?? file.file?.name),
      }));

      if (!isEmptyString(mediaTypeKey)) {
        setFilesByMediaType((previousFilesByMediaType) => ({
          ...previousFilesByMediaType,
          [mediaTypeKey]: updatedFileList,
        }));
      }

      if (invalidFiles.length > 0) {
        const invalidFileNames = invalidFiles.map((file) => file.file?.name).join(', ');
        notify({
          variant: 'error',
          message: `Invalid ${pluralize('file', invalidFiles.length, false)}: ${invalidFileNames}`,
        });
      }
    },
    [allowedFileTypes, notify],
  );

  useEffect(() => {
    const hasCategories = tabOptions.length > 0;

    if (hasCategories) {
      const nextCategory = defaultCategory ?? tabOptions[0];
      const hasSelectedCategory = tabOptions.includes(selectedCategory);

      if (!hasSelectedCategory) {
        selectCategory(nextCategory);
      }
    }
  }, [defaultCategory, selectedCategory, tabOptions]);

  useEffect(() => {
    const unsubscribe = mediaEventChannel.on(
      'onMediaUploadedToCategory',
      ({ original, files }) => { updateData(original, files); },
    );
    return () => {
      unsubscribe();
    };
  }, [updateData]);

  const isAnyMediaSelected = useMemo(
    () => mediaTypesWithFiles.some((item) => (item?.files ?? []).length > 0),
    [mediaTypesWithFiles],
  );

  const filteredData = useMemo(() => mediaTypesWithFiles.filter(
    (item) => (item?.category === selectedCategory || selectedCategory === 'All'),
  ), [mediaTypesWithFiles, selectedCategory]);

  const totalSelectedFiles = useMemo(() => (
    mediaTypesWithFiles.reduce((sum, item) => sum + ((item?.files ?? []).length), 0)
  ), [mediaTypesWithFiles]);
  const isLimitReached = totalSelectedFiles >= 10;
  const fileUploadDialogContextValue = useMemo(
    () => ({ isSaving, isLimitReached }),
    [isSaving, isLimitReached],
  );

  const uploadMedia = async (data: MultimediaTypeWithCategory[]) => {
    setUploadErrors([]);
    const mediaList = data.flatMap((item) => (
      (item?.files ?? []).map((file) => ({
        multimediaType: item?.value ?? '',
        dataUrl: file?.dataUrl ?? '',
        fileName: sanitizeFileName(file?.file?.name ?? ''),
      }))
    ));

    try {
      const result = await uploadBulk({
        patientId,
        mediaList,
      });

      const uploadedIds = result?.uploadedMediaIds ?? [];
      onUpdate?.(uploadedIds.map((id) => ({
        patientMultiMediaId: id,
      })));

      const errors = compact(result?.errors);
      const hasErrors = errors.length > 0;

      if (hasErrors) {
        setUploadErrors(errors);
      } else {
        notify({
          variant: 'success',
          message: 'Media uploaded successfully',
        });
        onClose?.();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const readableAllowedFileTypes = useMemo(
    () => allowedFileTypes.split(',').map((ext) => ext.trim().replace(/^\./, '')).join(', '),
    [allowedFileTypes],
  );

  return (
    <Dialog
      open
      title="Upload Media"
      onClose={onClose}
      onConfirm={noop}
      maxWidth="xl"
      fullWidth
      className="overflow-y-hidden overflow-x-hidden !space-y-0 gap-2 !w-full"
      dialogClassName={clsx({ 'hidden-backdrop': !hasBackdrop })}
      classes={{ bodyRoot: '!max-w-[62rem]' }}
      minHeight={dialogHeight}
      disableEnforceFocus
    >
      <div className={clsx('flex flex-1 flex-col populate-form min-h-[10rem] gap-form-row w-full')}>
        <div className="flex flex-col gap-form-row !w-full">
          <div className="flex flex-col items-start gap-4">
            <TabFilter
              options={tabOptions}
              defaultTab={defaultCategory}
              onUpdate={(option) => {
                selectCategory(option);
              }}
              variant="minimal"
              className="w-full"
            />
            <Alert
              open={uploadErrors?.length > 0}
              severity="error"
              className="w-full border-red-400 border rounded-lg p-2"
              icon={false}
            >
              <div className="mb-2 text-xs">Please review or remove the following files before saving:</div>
              <ul className="list-disc pl-5">
                {uploadErrors?.map((err) => (
                  <li key={err.fileName} className="!text-xs">
                    <span className="font-semibold">{err.fileName}</span>
                    {' '}
                    (uploaded for
                    {' '}
                    <span className="font-semibold">
                      {err.mediaType}
                    </span>
                    ):
                    {' '}
                    {err.error}
                  </li>
                ))}
              </ul>
            </Alert>
          </div>
          <SelectableProvider isSolo>
            <div className="min-w-0 overflow-x-auto">
              <div className="flex min-w-[720px] flex-row gap-4">
                <div className="w-1/2 h-[28rem] rounded-lg flex flex-col gap-2">
                  <h1 className="text-sm font-semibold">{selectedCategory}</h1>
                  <div className="border rounded-lg border-gray-200 overflow-hidden flex-1">
                    <FileUploadDialogContext.Provider value={fileUploadDialogContextValue}>
                      <MediaTypesList
                        data={filteredData}
                        dropzoneAccept={dropzoneAccept}
                        disabled={isSaving}
                      />
                    </FileUploadDialogContext.Provider>
                  </div>
                </div>
                <div className="w-1/2 h-[28rem] rounded-lg flex flex-col gap-2">
                  <h1 className="text-sm font-semibold">Preview</h1>
                  <div className="border rounded-lg border-gray-200 flex-1 bg-gray-50 overflow-auto">
                    {!isAnyMediaSelected ? (
                      <div className="flex flex-col items-center justify-center h-full">
                        <p className="text-gray-500 text-sm text-center">
                          Preview will appear here
                        </p>
                      </div>
                    ) : (
                      <Preview data={mediaTypesWithFiles} onUpdate={updateData} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SelectableProvider>
        </div>
      </div>
      <div className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <div className="text-xs font-medium text-gray-500">* Select files or drop on the media type to preview</div>
          <div className="text-xs font-medium text-gray-500">
            * Supported formats:
            {' '}
            <span className="italic">{readableAllowedFileTypes}</span>
          </div>
        </div>
        <div className="flex justify-end items-center gap-2 pt-4">
          <Button
            variant="outlined"
            type="button"
            onClick={onClose}
          >
            Close
          </Button>
          <ToolTip
            title={isLimitReached ? 'Maximum 10 files can be uploaded at a time.' : 'Please upload at least one media to save'}
            hide={isAnyMediaSelected && totalSelectedFiles <= 10}
          >
            <span>
              <Button
                color="primary"
                variant="contained"
                type="button"
                disabled={isSaving || !isAnyMediaSelected || totalSelectedFiles > 10}
                onClick={() => {
                  uploadMedia(mediaTypesWithFiles.filter(
                    (item) => (item?.files ?? []).length > 0,
                  ));
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </span>
          </ToolTip>
        </div>
      </div>
    </Dialog>
  );
}
